import axios, { AxiosError, AxiosRequestConfig } from 'axios';

/** Error shape from the API (PRD 7.2): { error: { code, message, details }, requestId }. */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Array<{ path: string; message: string }> | unknown,
    public requestId?: string,
  ) {
    super(message);
  }
  /** field-level messages keyed by field path, for forms */
  get fields(): Record<string, string> {
    return Array.isArray(this.details)
      ? Object.fromEntries((this.details as Array<{ path: string; message: string }>).filter((d) => d?.path).map((d) => [d.path, d.message]))
      : {};
  }
}

export const errorMessage = (e: unknown): string =>
  e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Something went wrong. Try again.';

let accessToken: string | null = null;
export const setAccessToken = (t: string | null) => {
  accessToken = t;
};
export const getAccessToken = () => accessToken;

/** Called when the session cannot be renewed; the auth store sets this to sign the user out. */
let onSessionLost: () => void = () => undefined;
export const setSessionLostHandler = (fn: () => void) => {
  onSessionLost = fn;
};

export const api = axios.create({ baseURL: '/api/v1', withCredentials: true, headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

const toApiError = (e: AxiosError<any>): ApiError => {
  const body = e.response?.data;
  if (body?.error) return new ApiError(e.response!.status, body.error.code, body.error.message, body.error.details, body.requestId);
  if (!e.response) return new ApiError(0, 'NETWORK_ERROR', 'Cannot reach the server. Check your connection and try again.');
  return new ApiError(e.response.status, 'HTTP_ERROR', `Request failed (${e.response.status}).`);
};

let refreshing: Promise<string | null> | null = null;

/** Rotate the refresh cookie into a new access token (SEC-05). Concurrent callers share one request. */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = axios
      .post('/api/v1/auth/refresh', {}, { withCredentials: true, headers: { 'X-Requested-With': 'fms-web' } })
      .then((r) => {
        accessToken = r.data.data.accessToken as string;
        return accessToken;
      })
      .catch(() => {
        accessToken = null;
        return null;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<any>) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const code = error.response?.data?.error?.code;
    const isAuthCall = original?.url?.includes('/auth/');
    if (error.response?.status === 401 && original && !original._retried && !isAuthCall && ['TOKEN_EXPIRED', 'UNAUTHORIZED'].includes(code)) {
      original._retried = true;
      const t = await refreshAccessToken();
      if (t) return api(original);
      onSessionLost();
    } else if (error.response?.status === 401 && ['SESSION_EXPIRED', 'SESSION_REVOKED', 'ACCOUNT_INACTIVE'].includes(code) && !isAuthCall) {
      onSessionLost();
    }
    return Promise.reject(toApiError(error));
  },
);

export interface Paged<T> {
  data: T[];
  meta: { total: number; page: number; limit: number; [k: string]: unknown };
}

/** GET that returns the `data` payload. */
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const r = await api.get(url, { params: clean(params) });
  return r.data.data as T;
}

export async function getPaged<T>(url: string, params?: Record<string, unknown>): Promise<Paged<T>> {
  const r = await api.get(url, { params: clean(params) });
  return { data: r.data.data as T[], meta: r.data.meta };
}

export async function send<T = unknown>(method: 'post' | 'put' | 'patch' | 'delete', url: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
  const r = await api.request({ method, url, data: body, headers });
  return (r.data?.data ?? undefined) as T;
}

/** A fresh Idempotency-Key per user action (payments, batch release; PRD 7.2). */
export const idempotencyKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

function clean(p?: Record<string, unknown>) {
  if (!p) return undefined;
  return Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}
